import React from "react";
import { DateTime } from "luxon";
import { toDateTimeLocalValue, fromDateTimeLocalValue } from "../../utils/dateUtils";

const DynamicField = ({ 
  column = {}, 
  value, 
  onChange, 
  suggestions = [], 
  // For CompanyDetails usage
  name,
  type
}) => {
  // If column is not provided but name is, create a synthetic column object
  if (!column || !column.accessor) {
    if (name) {
      // CompanyDetails usage
      return (
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange && onChange(e.target.value)}
          className="default-input"
        />
      );
    }
    
    // Fallback for unexpected usage
    console.warn("DynamicField: column prop is missing accessor and no name provided");
    return (
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange && onChange(e.target.value)}
        className="default-input"
      />
    );
  }

  if (column.accessor === "vip") {
    return (
      <input
        type="checkbox"
        checked={!!value}  
        onChange={(e) => onChange(e.target.checked ? 1 : 0)}
      />
    );
  }

  if (column.DropdownOptions) {
    const backgroundColor = column.dropdownBackgroundColor
      ? column.dropdownBackgroundColor(value)
      : "#fff";
    return (
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        style={{
          backgroundColor,
        }}
      >
        {column.DropdownOptions.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }

  if (column.datePicker) {
    // Use our utility to convert UTC to local datetime-local input format
    const localDateTime = value ? toDateTimeLocalValue(value) : "";
    return (
      <input
        type="datetime-local"
        value={localDateTime}
        onChange={(e) => {
          // Use our utility to convert local input to UTC
          const isoDate = fromDateTimeLocalValue(e.target.value);
          onChange(isoDate);
        }}
      />
    );
  }

  if (column.autocomplete) {
    return (
      <>
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          list={`autocomplete-${column.accessor}`}
        />
        {suggestions.length > 0 && (
          <datalist id={`autocomplete-${column.accessor}`}>
            {suggestions.map((item, index) => (
              <option key={index} value={item} />
            ))}
          </datalist>
        )}
      </>
    );
  }

  if (column.chips) {
    const chips =
      Array.isArray(value)
        ? value
        : typeof value === "string"
        ? value.split(",").map((chip) => chip.trim())
        : [];
    return (
      <div>
        {chips.map((chip, index) => (
          <span
            key={index}
            style={{
              display: "inline-block",
              padding: "2px 6px",
              margin: "2px",
              backgroundColor: "#e0e0e0",
              borderRadius: "4px",
            }}
          >
            {chip}
          </span>
        ))}
        <input
          type="text"
          value={typeof value === "string" ? value : chips.join(", ")}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter comma-separated values"
        />
      </div>
    );
  }

  if (column.chipDropdown) {
    const chipColor = column.chipDropdownChipColor
      ? column.chipDropdownChipColor({ value })
      : "#e0e0e0";
    const textColor =
      chipColor && chipColor.startsWith("#")
        ? (parseInt(chipColor.slice(1), 16) > 0xffffff / 2 ? "black" : "white")
        : "black";
    return (
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        style={{
          backgroundColor: chipColor,
          color: textColor,
          border: "none",
          borderRadius: "12px",
          padding: "2px 8px",
          fontSize: "12px",
          WebkitAppearance: "none",
          MozAppearance: "none",
          appearance: "none",
        }}
      >
        {column.chipDropdownOptions &&
          column.chipDropdownOptions.map((option) => (
            <option
              key={option[column.chipDropdownValueField]}
              value={option[column.chipDropdownValueField]}
            >
              {column.chipDropdownDisplay
                ? column.chipDropdownDisplay(option)
                : option[column.chipDropdownValueField]}
            </option>
          ))}
      </select>
    );
  }

  return (
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

export default DynamicField;
